/**
 * Utility to defer non-critical JavaScript execution until after paint
 * This helps reduce main thread blocking during initial page load
 */

/**
 * Schedules a function to run after the next frame paint
 * Use this for non-critical initialization code
 */
export const deferScript = (callback: () => void): void => {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(callback, { timeout: 2000 });
  } else {
    // Fallback for browsers without requestIdleCallback
    requestAnimationFrame(() => {
      setTimeout(callback, 1);
    });
  }
};

/**
 * Break up long tasks by yielding to the main thread
 * Use this inside loops or heavy computations
 */
export const yieldToMain = (): Promise<void> => {
  return new Promise(resolve => {
    setTimeout(resolve, 0);
  });
};

/**
 * Process items in chunks to avoid blocking main thread
 */
export const processInChunks = async <T>(
  items: T[],
  processItem: (item: T) => void | Promise<void>,
  chunkSize: number = 5
): Promise<void> => {
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    
    for (const item of chunk) {
      await processItem(item);
    }
    
    // Yield to main thread after each chunk
    if (i + chunkSize < items.length) {
      await yieldToMain();
    }
  }
};
